import { useState, useRef, useCallback, useEffect } from 'react'
import type { PickedPhoto } from 'components/PhotoBank/PhotoBank.types'
import type {
  GooglePhotosSession,
  GooglePhotosMediaItem,
  GooglePhotosAuthConfig,
  GooglePhotosDownloadProgress,
  GooglePhotosPickingOptions,
  GooglePhotosTokenInfo,
  UseGooglePhotosReturn,
} from './GooglePhotosPicker.types'
import { GooglePhotosStatus } from './GooglePhotosPicker.types'
import { throwGooglePhotosError } from './GooglePhotosErrorHandler'
import { analytics, EVENT_NAMES } from './Analytics/Analytics'

declare global {
  interface Window {
    google: any
  }
}

// As per https://developers.google.com/photos/overview/authorization
const SCOPES = [
  'https://www.googleapis.com/auth/photospicker.mediaitems.readonly',
]

const GOOGLE_PHOTOS_PICKER_API_BASE_URL =
  'https://photospicker.googleapis.com/v1'

const DEFAULT_POLL_INTERVAL = 2

let pickerWindow: Window | null = null

// Implements the Google Photos Picker flow from here:
// https://developers.google.com/photos/picker/guides/get-started-picker
export function useGooglePhotos(): UseGooglePhotosReturn {
  const [tokenInfo, setTokenInfo] = useState<GooglePhotosTokenInfo | null>(null)
  const [status, setStatus] = useState<GooglePhotosStatus | null>(null)
  const [downloadProgress, setDownloadProgress] =
    useState<GooglePhotosDownloadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingPickingOptions, setPendingPickingOptions] =
    useState<GooglePhotosPickingOptions | null>(null)
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentPromiseRef = useRef<{
    resolve: (photos: PickedPhoto[]) => void
    reject: (error: Error) => void
  } | null>(null)
  const completionCallbackRef = useRef<
    ((photos: PickedPhoto[]) => void) | null
  >(null)

  const cleanup = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }, [])

  const resetAllStates = useCallback(() => {
    setTokenInfo(null)
    setStatus(null)
    setDownloadProgress(null)
    setError(null)
    setPendingPickingOptions(null)
  }, [])

  const reset = useCallback(() => {
    resetAllStates()
    completionCallbackRef.current = null
    cleanup()
    if (currentPromiseRef.current) {
      currentPromiseRef.current.reject(new Error('Operation reset'))
      currentPromiseRef.current = null
    }
  }, [cleanup, resetAllStates])

  useEffect(() => {
    return cleanup
  }, [cleanup])

  const isTokenExpired = useCallback(
    (token: GooglePhotosTokenInfo): boolean => {
      if (!token.expiresAt) {
        // If we dont have expiry assume expired to be safe
        return true
      }
      const now = Date.now()
      // We want to check 5 minutes before the token expires to be safe with starting the flow
      const fiveMinutesFromNow = now + 5 * 60 * 1000
      return token.expiresAt <= fiveMinutesFromNow
    },
    []
  )

  const checkTokenExpiry = useCallback(() => {
    if (tokenInfo && isTokenExpired(tokenInfo)) {
      resetAllStates()
    }
  }, [tokenInfo, isTokenExpired, resetAllStates])

  useEffect(() => {
    if (!tokenInfo) return

    const interval = setInterval(checkTokenExpiry, 60000) // Check every minute
    return () => clearInterval(interval)
  }, [tokenInfo, checkTokenExpiry])

  const authenticate = useCallback(
    (
      config: GooglePhotosAuthConfig,
      onSuccess: (tokenInfo: GooglePhotosTokenInfo) => void,
      onError: () => void
    ): void => {
      const originalOpen = window.open
      window.open = function auth(url, name, features) {
        if (url && url.toString().includes('accounts.google.com')) {
          const width = 500
          const height = 600
          const left = window.screen.width / 2 - width / 2
          const top = window.screen.height / 2 - height / 2
          const centeredFeatures = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
          return originalOpen.call(this, url, name, centeredFeatures)
        }
        return originalOpen.call(this, url, name, features)
      }

      window.google.accounts.oauth2
        .initTokenClient({
          client_id: config.clientId,
          scope: SCOPES.join(' '),
          prompt: 'consent',
          callback: (response: any) => {
            window.open = originalOpen

            if (response.access_token) {
              const now = Date.now()
              const expiresAt = response.expires_in
                ? now + response.expires_in * 1000
                : undefined

              const tokenInfo: GooglePhotosTokenInfo = {
                accessToken: response.access_token,
                expiresAt,
              }

              // Track successful authentication
              analytics.track(EVENT_NAMES.GOOGLE_PHOTOS_AUTHENTICATED, {
                expiresAt,
                expiresInSeconds: response.expires_in,
                expiresInHours: response.expires_in
                  ? (response.expires_in / 3600).toFixed(2)
                  : undefined,
                hasExpiryData: !!expiresAt,
              })

              setTokenInfo(tokenInfo)
              onSuccess(tokenInfo)
            } else {
              onError()
            }
          },
        })
        .requestAccessToken()
    },
    []
  )

  // https://developers.google.com/photos/picker/guides/sessions#create-sessions
  const createPickerSession = useCallback(
    async (
      token: string,
      options: GooglePhotosPickingOptions
    ): Promise<GooglePhotosSession> => {
      const requestBody: any = {}

      requestBody.pickingConfig = {
        maxItemCount: options.singleFileOnly ? '1' : '200',
      }

      requestBody.pollingConfig = {
        pollInterval: `${DEFAULT_POLL_INTERVAL}s`,
      }

      const response = await fetch(
        `${GOOGLE_PHOTOS_PICKER_API_BASE_URL}/sessions`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()

        throwGooglePhotosError(
          'SESSION_CREATION_FAILED',
          'creating_session',
          `Failed to create picker session: ${response.status} ${response.statusText}`,
          null,
          {
            statusCode: response.status,
            statusText: response.statusText,
            responseText: errorText,
          },
          {
            httpStatus: response.status,
            onTokenExpired: resetAllStates,
          }
        )
      }

      const session = await response.json()

      if (!session.pickerUri || !session.id) {
        throwGooglePhotosError(
          'INVALID_SESSION_RESPONSE',
          'creating_session',
          'Invalid session response - missing pickerUri or id',
          null,
          {
            sessionData: JSON.stringify(session),
          }
        )
      }

      return session
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // https://developers.google.com/photos/picker/guides/sessions#poll-sessions
  const pollSessionStatus = useCallback(
    async (token: string, sessionId: string): Promise<GooglePhotosSession> => {
      const response = await fetch(
        `${GOOGLE_PHOTOS_PICKER_API_BASE_URL}/sessions/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        throwGooglePhotosError(
          'POLL_SESSION_FAILED',
          'polling_session',
          `Failed to poll session status: ${response.status} ${response.statusText}`,
          null,
          {
            statusCode: response.status,
            statusText: response.statusText,
            sessionId,
          },
          {
            httpStatus: response.status,
            onTokenExpired: resetAllStates,
          }
        )
      }

      return await response.json()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // https://developers.google.com/photos/picker/guides/media-items#list_picked_media_items
  const fetchSelectedMediaItems = useCallback(
    async (
      token: string,
      sessionId: string
    ): Promise<GooglePhotosMediaItem[]> => {
      let allMediaItems: GooglePhotosMediaItem[] = []
      let nextPageToken: string | undefined
      let hasMorePages = true

      while (hasMorePages) {
        const url = new URL(`${GOOGLE_PHOTOS_PICKER_API_BASE_URL}/mediaItems`)
        url.searchParams.append('sessionId', sessionId)
        if (nextPageToken) {
          url.searchParams.append('pageToken', nextPageToken)
        }

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throwGooglePhotosError(
            'FETCH_MEDIA_ITEMS_FAILED',
            'fetching_media_items',
            `Failed to fetch selected items: ${response.status} ${response.statusText}`,
            null,
            {
              statusCode: response.status,
              statusText: response.statusText,
              sessionId,
            },
            {
              httpStatus: response.status,
              onTokenExpired: resetAllStates,
            }
          )
        }

        const data = await response.json()
        const mediaItems = data.mediaItems || []
        allMediaItems = [...allMediaItems, ...mediaItems]
        nextPageToken = data.nextPageToken
        hasMorePages = !!nextPageToken
      }

      allMediaItems = allMediaItems.filter((item) => {
        return item.type === 'PHOTO'
      })

      return allMediaItems
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // https://developers.google.com/photos/picker/guides/media-items
  const downloadMediaItemsAsFiles = useCallback(
    async (
      token: string,
      mediaItems: GooglePhotosMediaItem[]
    ): Promise<PickedPhoto[]> => {
      const files: File[] = []

      for (const [index, item] of mediaItems.entries()) {
        setDownloadProgress({ current: index + 1, total: mediaItems.length })

        try {
          const response = await fetch(`${item.mediaFile.baseUrl}=d`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          if (!response.ok) {
            try {
              throwGooglePhotosError(
                'MEDIA_DOWNLOAD_FAILED',
                'downloading_media',
                `Failed to download media item: ${response.statusText}`,
                null,
                {
                  statusCode: response.status,
                  statusText: response.statusText,
                  filename: item.mediaFile.filename,
                  url: item.mediaFile.baseUrl,
                },
                {
                  httpStatus: response.status,
                  onTokenExpired: resetAllStates,
                }
              )
            } catch {
              // Continue processing other items even if one fails
              continue
            }
          }

          const blob = await response.blob()
          const file = new File([blob], item.mediaFile.filename, {
            type: blob.type || 'image/jpeg',
          })
          files.push(file)
        } catch (error) {
          try {
            throwGooglePhotosError(
              'MEDIA_DOWNLOAD_EXCEPTION',
              'downloading_media',
              `Error downloading media item: ${item.mediaFile.filename}`,
              error,
              {
                filename: item.mediaFile.filename,
                url: item.mediaFile.baseUrl,
              }
            )
          } catch {
            // Continue processing other items even if one fails
          }
        }
      }

      return files
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // Parse pollInterval which comes as a string like "5s"
  const parsePollInterval = (interval: string | undefined): number => {
    if (!interval || interval === '5s') return DEFAULT_POLL_INTERVAL // Use default if no interval is provided or it's 5s because we want to make waiting time shorter than the default Google Photos give
    const match = interval.match(/^(\d+)s$/)
    return match ? parseInt(match[1], 10) : DEFAULT_POLL_INTERVAL
  }

  const pollUntilComplete = useCallback(
    async (token: string, sessionId: string): Promise<void> => {
      try {
        const data = await pollSessionStatus(token, sessionId)

        if (data.mediaItemsSet) {
          setStatus(GooglePhotosStatus.FETCHING_IMAGES)
          const mediaItems = await fetchSelectedMediaItems(token, sessionId)
          const downloadedPhotos = await downloadMediaItemsAsFiles(
            token,
            mediaItems
          )

          setStatus(null)
          setDownloadProgress(null)
          setError(null)
          pickerWindow?.close()

          // Call the persistent completion callback first
          if (completionCallbackRef.current) {
            completionCallbackRef.current(downloadedPhotos)
            completionCallbackRef.current = null
          }

          // Then resolve the promise if it exists
          if (currentPromiseRef.current) {
            currentPromiseRef.current.resolve(downloadedPhotos)
            currentPromiseRef.current = null
          }
        } else {
          const delay =
            parsePollInterval(data.pollingConfig?.pollInterval) * 1000
          pollTimeoutRef.current = setTimeout(() => {
            pollUntilComplete(token, sessionId)
          }, delay)
        }
      } catch (error) {
        setStatus(null)
        try {
          throwGooglePhotosError(
            'SESSION_POLLING_ERROR',
            'polling_session',
            'Error polling session status',
            error
          )
        } catch {
          // Error has been tracked, now handle it
          setError('Error polling session status')
        }

        if (currentPromiseRef.current) {
          currentPromiseRef.current.reject(error as Error)
          currentPromiseRef.current = null
        }
      }
    },
    [pollSessionStatus, fetchSelectedMediaItems, downloadMediaItemsAsFiles]
  )

  const handlePickingFlow = useCallback(
    async (
      token: string,
      options: GooglePhotosPickingOptions
    ): Promise<void> => {
      try {
        // Open the window immediately to prevent popup blockers
        pickerWindow = window.open(
          'about:blank',
          'photoPicker',
          'fullscreen=yes,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes'
        )

        if (!pickerWindow) {
          throw new Error('Popup blocked or failed to open')
        }

        setStatus(GooglePhotosStatus.CREATING_SESSION)
        const session = await createPickerSession(token, options)

        // Navigate the already-open window to the picker URI
        pickerWindow.location.href = `${session.pickerUri}/autoclose`
        setStatus(GooglePhotosStatus.AUTHENTICATED)

        await pollUntilComplete(token, session.id)
      } catch (error) {
        // Close the window if something went wrong
        if (pickerWindow) {
          pickerWindow.close()
          pickerWindow = null
        }

        setStatus(null)
        try {
          throwGooglePhotosError(
            'PICKING_FLOW_ERROR',
            'photo_picker_flow',
            'Error in photo picking flow',
            error
          )
        } catch {
          // Error has been tracked, now handle it
          setError('Error in photo picking flow')
        }

        if (currentPromiseRef.current) {
          currentPromiseRef.current.reject(error as Error)
          currentPromiseRef.current = null
        }
      }
    },
    [createPickerSession, pollUntilComplete]
  )

  const startPhotoPicking = useCallback(
    async (
      config: GooglePhotosAuthConfig,
      options: GooglePhotosPickingOptions = {},
      onAuthSuccess?: () => Promise<void>
    ): Promise<PickedPhoto[]> => {
      setError(null)

      return new Promise<PickedPhoto[]>((resolve, reject) => {
        currentPromiseRef.current = { resolve, reject }

        const executeFlow = async () => {
          try {
            if (tokenInfo && !isTokenExpired(tokenInfo)) {
              setStatus(GooglePhotosStatus.AUTHENTICATED)
              setPendingPickingOptions(options)
              if (onAuthSuccess) {
                await onAuthSuccess()
              }
            } else {
              setStatus(GooglePhotosStatus.AUTHENTICATING)
              await authenticate(
                config,
                async (_tokenInfo) => {
                  setStatus(GooglePhotosStatus.AUTHENTICATED)
                  setPendingPickingOptions(options)
                  if (onAuthSuccess) {
                    await onAuthSuccess()
                  }
                },
                () => {
                  setStatus(null)
                  try {
                    throwGooglePhotosError(
                      'AUTHENTICATION_FAILED',
                      'authenticating',
                      'Authentication failed or cancelled'
                    )
                  } catch {
                    // Error has been tracked, now handle it
                    setError('Authentication failed or cancelled')
                  }
                  if (currentPromiseRef.current) {
                    currentPromiseRef.current.reject(
                      new Error('Authentication failed')
                    )
                    currentPromiseRef.current = null
                  }
                }
              )
            }
          } catch (error) {
            setStatus(null)
            try {
              throwGooglePhotosError(
                'START_PICKING_ERROR',
                'initial_flow_setup',
                'Error in initial photo picking flow setup',
                error
              )
            } catch {
              // Error has been tracked, now handle it
              setError('Error in initial photo picking flow setup')
            }

            if (currentPromiseRef.current) {
              currentPromiseRef.current.reject(error as Error)
              currentPromiseRef.current = null
            }
          }
        }

        executeFlow()
      })
    },
    [tokenInfo, isTokenExpired, authenticate]
  )

  const proceedWithPendingPicking = useCallback(async () => {
    if (!tokenInfo || !pendingPickingOptions) {
      return
    }

    await handlePickingFlow(tokenInfo.accessToken, pendingPickingOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenInfo?.accessToken, pendingPickingOptions, handlePickingFlow])

  const setCompletionCallback = useCallback(
    (callback: (photos: PickedPhoto[]) => void) => {
      completionCallbackRef.current = callback
    },
    []
  )

  const logout = useCallback(() => {
    setTokenInfo(null)

    if (pickerWindow) {
      pickerWindow.close()
      pickerWindow = null
    }

    reset()
  }, [reset])

  return {
    startPhotoPicking,
    proceedWithPendingPicking,
    setCompletionCallback,
    status,
    downloadProgress,
    error,
    hasToken: !!tokenInfo?.accessToken,
    hasPendingPicking: !!pendingPickingOptions,
    reset,
    logout,
  }
}
